import { useEffectEvent } from '@react-aria/utils';
import isEqual from 'fast-deep-equal/es6/react';
import { useEffect, useRef } from 'react';

import { useSession } from './useSession';

import type { Session } from '../types';

export type OnSessionChangeCallback = (
  prevSession: Session | null | undefined,
  currentSession: Session | null
) => void;

/**
 * Hook that triggers a callback when the user session changes.
 *
 * Note that the callback will always be called at least once: undef => session|null
 *
 * @param callback Function to be called with previous and current sessions as arguments.
 */
export function useOnSessionChange(callback: OnSessionChangeCallback) {
  const { session } = useSession();
  const previousSession = useRef<Session | null | undefined>(undefined);

  const onSessionChange = useEffectEvent<OnSessionChangeCallback>(
    (prev, curr) => {
      callback(prev, curr);
    }
  );

  useEffect(() => {
    if (isEqual(previousSession.current, session)) return;
    if (session === undefined) {
      // for TS, should never happen by design
      return;
    }

    onSessionChange(previousSession.current, session);
    previousSession.current = session;
  }, [session, onSessionChange]);
}
